import React, { useMemo } from 'react';
import { PANEL_GAP, PANEL_PADDING } from '../../utils/layout';
import { Autocomplete, Box, Button, ListSubheader, MenuItem, Paper, TextField } from '@mui/material';
import { FilterAltOff as ClearIcon } from '@mui/icons-material';
import { formatDateShort } from '../../utils/formatters';
import { EMPTY_FILTERS, datePresets, hasFilters, matchSiteAndDate, matchingPreset, siteNames } from '../../utils/surveyFilters';

const surveyLabel = (s) => `${s.location || 'Unnamed survey'} · ${formatDateShort(s.survey_date)}`;
const newestFirst = (a, b) => ((a.survey_date || '') < (b.survey_date || '') ? 1 : -1);

/**
 * Site / survey / date-range filters for anything built from the survey list.
 * Controlled: `filters` comes from utils/surveyFilters and `onChange` gets the
 * next value. The survey picker only offers surveys that match the chosen
 * sites and dates. "Dates" offers one-click ranges (recent periods and each
 * year with surveys) that fill in From / To; editing those makes it "Custom".
 */
const ANY_TIME = 'any';
const CUSTOM = 'custom';

const SurveyFilterBar = ({ surveys, filters, onChange }) => {
  const sites = useMemo(() => siteNames(surveys), [surveys]);
  const surveyOptions = useMemo(() => [...matchSiteAndDate(surveys, filters)].sort(newestFirst), [surveys, filters]);
  const pickedSurveys = useMemo(
    () => surveys.filter((s) => filters.surveyIds.includes(s.id)).sort(newestFirst),
    [surveys, filters.surveyIds]
  );
  const presets = useMemo(() => datePresets(surveys), [surveys]);
  const preset = matchingPreset(filters, presets);
  let dateChoice = preset?.key || CUSTOM;
  if (!filters.from && !filters.to) dateChoice = ANY_TIME;
  const update = (changes) => onChange({ ...filters, ...changes });
  const choosePreset = (key) => {
    if (key === ANY_TIME) update({ from: '', to: '' });
    const chosen = presets.find((p) => p.key === key);
    if (chosen) update({ from: chosen.from, to: chosen.to });
  };
  const recent = presets.filter((p) => !p.group);
  const years = presets.filter((p) => p.group === 'years');

  return (
    <Paper sx={{ p: PANEL_PADDING, mb: PANEL_GAP }}>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          alignItems: 'center',
          // Phones: sites and surveys full width, then the dates in pairs.
          // Wide screens: everything on one row.
          // minmax(0, …) and minWidth 0 let fields shrink: date inputs and the
          // pickers otherwise insist on their natural width and overflow phones.
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', lg: 'minmax(0, 1.4fr) minmax(0, 1.6fr) 170px 150px 150px auto' },
          '& > *': { minWidth: 0 },
          '& > .full': { gridColumn: { xs: '1 / -1', sm: 'span 3', lg: 'auto' } },
        }}
      >
        <Box className="full">
          <Autocomplete
            multiple
            size="small"
            limitTags={1}
            options={sites}
            value={filters.sites}
            onChange={(_e, next) => update({ sites: next })}
            renderInput={(params) => <TextField {...params} label="Sites" placeholder={filters.sites.length ? '' : 'All sites'} />}
          />
        </Box>
        <Box className="full">
          <Autocomplete
            multiple
            size="small"
            limitTags={1}
            options={surveyOptions}
            value={pickedSurveys}
            getOptionLabel={surveyLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_e, next) => update({ surveyIds: next.map((s) => s.id) })}
            noOptionsText="No surveys match the sites and dates"
            renderInput={(params) => (
              <TextField {...params} label="Surveys" placeholder={pickedSurveys.length ? '' : 'All surveys'} />
            )}
          />
        </Box>
        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
          <TextField
            select
            size="small"
            label="Dates"
            fullWidth
            value={dateChoice}
            onChange={(e) => choosePreset(e.target.value)}
          >
            <MenuItem value={ANY_TIME}>Any time</MenuItem>
            {recent.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
            {years.length > 0 && <ListSubheader>Earlier years</ListSubheader>}
            {years.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
            {/* Shown (not chosen) when From / To were set by hand. */}
            <MenuItem value={CUSTOM} sx={{ display: 'none' }}>Custom range</MenuItem>
          </TextField>
        </Box>
        <Box>
          <TextField
            type="date"
            size="small"
            label="From"
            fullWidth
            value={filters.from}
            onChange={(e) => update({ from: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: filters.to || undefined }}
          />
        </Box>
        <Box>
          <TextField
            type="date"
            size="small"
            label="To"
            fullWidth
            value={filters.to}
            onChange={(e) => update({ to: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: filters.from || undefined }}
          />
        </Box>
        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 3', lg: 'auto' }, display: 'flex', justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
          <Button
            size="small"
            color="inherit"
            startIcon={<ClearIcon />}
            disabled={!hasFilters(filters)}
            onClick={() => onChange(EMPTY_FILTERS)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Clear
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default SurveyFilterBar;
